import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider, useAuth } from './providers/AuthProvider'
import { StandardsProvider } from './providers/StandardsProvider'
import { ActiveExperimentProvider } from './providers/ActiveExperimentProvider'
import theme from './theme'

import LoginPage from './pages/login'
import StandardsPage from './pages/standards'
import StandardDetailPage from './pages/standard-detail'
import KUDetailPage from './pages/ku-detail'
import DatasetsPage from './pages/datasets'
import DatasetDetailPage from './pages/datasets/detail'
import AnalyticsPage from './pages/analytics'
import WorkbenchPage from './pages/workbench'
import ProfilePage from './pages/profile'
import AboutPage from './pages/about'
import ExperimentReviewPage from './pages/experiments/review'
import ExperimentAnalysisPage from './pages/experiments/analysis'
import AdminPage from './pages/admin'
import MyReviewsPage from './pages/my-reviews'
import ResetPasswordPage from './pages/reset-password'
import AppShell from './components/layout/AppShell'
import { Box, CircularProgress } from '@mui/material'
import { ROLE_CAPS } from './types'

function ProtectedRoute({
  children,
  requireCap,
}: {
  children: React.ReactNode
  requireCap?: keyof (typeof ROLE_CAPS)['admin']
}) {
  const { session, profile, loading } = useAuth()

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!session) return <Navigate to="/login" replace />
  // Experts are routed into their own review space and never to /standards.
  const fallbackPath = profile?.role === 'expert' ? '/my-reviews' : '/standards'
  if (requireCap && profile && !ROLE_CAPS[profile.role][requireCap]) return <Navigate to={fallbackPath} replace />

  return <>{children}</>
}

function RoleRedirect() {
  const { session, profile, loading } = useAuth()
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }
  if (!session) return <Navigate to="/login" replace />
  return <Navigate to={profile?.role === 'expert' ? '/my-reviews' : '/standards'} replace />
}

function ShellRoute({
  children,
  requireCap,
}: {
  children: React.ReactNode
  requireCap?: keyof (typeof ROLE_CAPS)['admin']
}) {
  return (
    <ProtectedRoute requireCap={requireCap}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  )
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={<RoleRedirect />} />
      <Route
        path="/standards"
        element={
          <ShellRoute>
            <StandardsPage />
          </ShellRoute>
        }
      />
      <Route
        path="/my-reviews"
        element={
          <ShellRoute requireCap="canReview">
            <MyReviewsPage />
          </ShellRoute>
        }
      />
      <Route
        path="/standard/:id"
        element={
          <ShellRoute>
            <StandardDetailPage />
          </ShellRoute>
        }
      />
      <Route
        path="/standard/:stdId/ku/:kuId"
        element={
          <ShellRoute>
            <KUDetailPage />
          </ShellRoute>
        }
      />
      <Route
        path="/datasets"
        element={
          <ShellRoute requireCap="canGenerate">
            <DatasetsPage />
          </ShellRoute>
        }
      />
      <Route
        path="/datasets/:id"
        element={
          <ShellRoute requireCap="canGenerate">
            <DatasetDetailPage />
          </ShellRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ShellRoute requireCap="canAnalytics">
            <AnalyticsPage />
          </ShellRoute>
        }
      />
      <Route
        path="/workbench"
        element={
          <ShellRoute requireCap="canPromptLab">
            <WorkbenchPage />
          </ShellRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ShellRoute>
            <ProfilePage />
          </ShellRoute>
        }
      />
      <Route
        path="/about"
        element={
          <ShellRoute>
            <AboutPage />
          </ShellRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ShellRoute requireCap="canAdmin">
            <AdminPage />
          </ShellRoute>
        }
      />
      <Route
        path="/experiments/:id/review"
        element={
          <ShellRoute requireCap="canReview">
            <ExperimentReviewPage />
          </ShellRoute>
        }
      />
      <Route
        path="/experiments/:id/analysis"
        element={
          <ShellRoute requireCap="canAnalytics">
            <ExperimentAnalysisPage />
          </ShellRoute>
        }
      />
    </Routes>
  )
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <StandardsProvider>
          <ActiveExperimentProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </ActiveExperimentProvider>
        </StandardsProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}
